import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Dropdown, Checkbox } from "semantic-ui-react";

const flattenChildren = (node) => {
    if (!node.children) return [];
    return node.children.flatMap((child) => [
        child.value,
        ...flattenChildren(child),
    ]);
};

const findParent = (value, options) => {
    for (const opt of options) {
        if (opt.children?.some((child) => child.value === value)) return opt;
        if (opt.children) {
            const found = findParent(value, opt.children);
            if (found) return found;
        }
    }
    return null;
};

const highlightLabel = (label, searchTerm) => {
    if (!searchTerm) return label;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = label?.split?.(regex);

    return parts?.map?.((part, index) =>
        regex.test(part) ? <strong key={index}>{part}</strong> : part
    );
};

// Recursive filter based on label match
const filterOptions = (options, searchTerm) => {
    return options
        .map((opt) => {
            const matches = opt.label
                .toLowerCase()
                .includes(searchTerm?.toLowerCase?.());
            if (opt?.children) {
                const filteredChildren = filterOptions(
                    opt?.children,
                    searchTerm
                );
                if (matches || filteredChildren?.length > 0) {
                    return { ...opt, children: filteredChildren };
                }
            } else if (matches) {
                return opt;
            }
            return null;
        })
        .filter(Boolean);
};

const CheckboxDropdownStaggered = ({
    label,
    name,
    options,
    onChange,
    value,
    onBlur,
    placeholder,
    disabled,
    selection,
    search,
    button,
}) => {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const filteredOptions = useMemo(() => {
        return searchTerm ? filterOptions(options, searchTerm) : options;
    }, [options, searchTerm]);

    const handleCheckboxChange = useCallback(
        (e, clickedValue, option) => {
            e.stopPropagation();
            let updatedValues = [...(value ?? [])];
            const isChecked = updatedValues?.includes?.(clickedValue);

            if (option.children) {
                const childValues = flattenChildren(option);
                if (isChecked) {
                    updatedValues = updatedValues?.filter?.(
                        (v) => !childValues?.includes?.(v) && v !== clickedValue
                    );
                } else {
                    updatedValues = Array.from(
                        new Set([
                            ...updatedValues,
                            clickedValue,
                            ...childValues,
                        ])
                    );
                }
            } else {
                if (isChecked) {
                    updatedValues = updatedValues.filter(
                        (v) => v !== clickedValue
                    );
                } else {
                    updatedValues.push(clickedValue);
                }

                const parent = findParent(clickedValue, options);
                if (parent) {
                    const childValues = parent?.children?.map?.(
                        (child) => child?.value
                    );
                    const allSelected = childValues?.every?.((val) =>
                        updatedValues.includes(val)
                    );

                    if (allSelected) {
                        updatedValues.push(parent.value);
                    } else {
                        updatedValues = updatedValues.filter(
                            (v) => v !== parent.value
                        );
                    }
                }
            }

            onChange(e, { name, value: updatedValues }, option);
        },
        [onChange, value, name, options]
    );

    const onSearch = (e, { searchQuery }) => {
        e?.stopPropagation?.();
        e?.preventDefault?.();
        setSearchTerm(searchQuery);
    };

    const renderOptions = useCallback(
        (opts, level = 0) =>
            opts.map((opt) => (
                <React.Fragment key={opt?.key}>
                    <Dropdown.Item
                        onClick={(e) =>
                            handleCheckboxChange(e, opt?.value, opt)
                        }
                        style={{
                            marginLeft: `${level * 24}px`,
                            padding: "6px 0",
                        }}
                    >
                        <Checkbox
                            label={highlightLabel(
                                opt?.label,
                                searchTerm || undefined
                            )}
                            checked={value?.includes(opt.value)}
                            onMouseDown={(e) => e.preventDefault()}
                            readOnly
                        />
                    </Dropdown.Item>
                    {opt?.children?.length > 0 &&
                        renderOptions(opt.children, level + 1)}
                </React.Fragment>
            )),
        [handleCheckboxChange, value, searchTerm]
    );

    return (
        <Dropdown
            text={label}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            className="CheckboxDropdown"
            selection={selection}
            button={button}
            search={search}
            onSearchChange={(e) => onSearch(e, { searchQuery: e.target.value })}
        >
            <Dropdown.Menu
                style={{
                    maxHeight: 300,
                    overflowY: "auto",
                    padding: "0.5rem",
                }}
            >
                {filteredOptions?.length ? (
                    renderOptions(filteredOptions)
                ) : (
                    <Dropdown.Item disabled>No results found.</Dropdown.Item>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

CheckboxDropdownStaggered.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ).isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            children: PropTypes.array,
        })
    ).isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    selection: PropTypes.bool,
    search: PropTypes.bool,
    button: PropTypes.bool,
};

CheckboxDropdownStaggered.defaultProps = {
    label: "Select",
    name: "filter",
    placeholder: "Select",
};

export default React.memo(CheckboxDropdownStaggered);
